import React, { useCallback } from 'react';
import { pure } from 'recompose';
import { useFocus } from 'js/hooks/useFocus';
import Image from 'js/components/common/Image';
import { Icon } from 'js/components/common/Icon';
import { useAppSelector, useUserSelector } from 'js/store/hooks';
import FeatureIcons from 'js/components/common/Game/FeatureIcons';
import { useLiveGameConfig, useGameImageUrl } from 'js/hooks/games';
import { FavoriteIcon } from 'js/components/common/Game/FavoriteIcon';
import { withTranslations } from 'js/components/hocs/withTranslations';
import { compose, path, pick, pipe, prop, identity } from 'js/utils/ramda.util';
import { usePortletConfig, usePortletSelector } from 'js/components/system/Portlet';

import { DemoButton, PlayButton } from '../Buttons';

import style from './index.scss';

export const Description = withTranslations(({ code, t }) => {
    const tKey = `playtech.mobile.system.gamedescription.${code}`;
    const MAX_LENGTH = 145;
    let value = t(tKey);

    value = value.includes(tKey)
        ? ''
        : value.length > MAX_LENGTH ? `${value.substring(0, MAX_LENGTH)}...` : value;

    return value && <p className={style.desc}>{value}</p>;
});

export const HoveredGame = ({
    t,
    code,
    flipToBack,
    setFlipRef,
    flipToFront,
    gameImageUrl,
    setGameInfoRef,
    showFavoriteIcon
}) => {
    const [cardBackRef, cardBackSetFocus] = useFocus();
    const gameFeed = usePortletSelector(prop('feedName'));
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const { name, funSupport, liveLaunchAlias } = useAppSelector(pipe(
        path(['games', code]),
        pick(['name', 'funSupport', 'liveLaunchAlias'])
    ));
    const { disableDemoWhenLoggedIn } = usePortletConfig();
    const showDemoButton = !(
        funSupport === 'unavailable'
        || isLoggedIn && (funSupport === 'guest' || disableDemoWhenLoggedIn)
    );

    const flipToBackExtended = useCallback(() => {
        flipToBack?.();

        cardBackSetFocus();
    }, [cardBackSetFocus, flipToBack]);

    const liveGameConfig = useLiveGameConfig(liveLaunchAlias);
    const isLiveGame = liveGameConfig.launchAlias;
    const { dynamicImageUrl, limits, openingDateTime, isScheduled } = liveGameConfig;
    const isScheduledLiveGame = isLiveGame && isScheduled;
    const { imageUrl, onError } = useGameImageUrl({ code, dynamicImageUrl, isLiveGame });

    return (
        <div ref={setGameInfoRef} className={style.gameInfoContainer}>
            <div ref={setFlipRef} className={style.flip}>
                <div className={style.front}>
                    <Image
                        alt={name}
                        className={style.image}
                        src={gameImageUrl || imageUrl}
                        onError={!gameImageUrl ? onError : identity}
                    />
                    <div className={`${isScheduledLiveGame ? style.scheduledLiveGameBackground : style.gameBackground}`} />
                    <div className={style.gameContent}>
                        <FeatureIcons
                            code={code}
                            types={['gameStudio']}
                            classNames={{ item: style.gameStudio }}
                        />
                        {!isLiveGame && (
                            <FeatureIcons
                                code={code}
                                tabIndex="0"
                                types={['volatility']}
                                onClick={flipToBackExtended}
                                classNames={{ item: style.volatility }}
                            />
                        )}
                        <h3 className={style.title}>{name}</h3>
                        <div className={style.buttons}>
                            {showDemoButton && !isLiveGame && (
                                <DemoButton code={code} name={name} isHoverBtn={true} />
                            )}
                            {isScheduledLiveGame
                                ? (
                                    <div className={style.scheduledLiveGameInfo}>
                                        {openingDateTime
                                            ? (
                                                <>
                                                    <span>{t('playtech.mobile.system.liveGame.startsAt')}</span>
                                                    <span>{openingDateTime}</span>
                                                </>
                                            )
                                            : <span>{t('playtech.mobile.system.liveGame.startsSoon')}</span>
                                        }
                                    </div>
                                )
                                : <PlayButton code={code} name={name} isHoverBtn={true} />
                            }
                        </div>
                        <Description code={code} />
                        <div className={style.bottomIcons}>
                            {isLiveGame && limits && <div className={style.liveLimits}>{limits}</div>}
                            <FeatureIcons code={code}
                                className={style.featuredIcons}
                                types={['fsb', 'goldenChips', 'wilds']}
                                classNames={{ item: style.featured }}
                            />
                            {showFavoriteIcon && (
                                <FavoriteIcon
                                    code={code}
                                    gameFeed={gameFeed}
                                    className={style.favorite}
                                />
                            )}
                        </div>
                    </div>
                </div>
                <div
                    tabIndex="-1"
                    ref={cardBackRef}
                    className={`${style.back} ${style.volatilityContent}`}
                >
                    <Icon
                        type="close_svg"
                        onClick={flipToFront}
                        className={style.close}
                        aria-label={t('playtech.mobile.system.popup.aria-label.close')}
                    />
                    <FeatureIcons
                        code={code}
                        showTitle={true}
                        types={['volatility']}
                        className={style.volatilityContainer}
                        classNames={{ image: style.volatility }}
                    />
                </div>
            </div>
        </div>
    );
};

export default compose(
    pure,
    withTranslations
)(HoveredGame);
